import PropTypes from 'prop-types';
import { Box, Divider, Stack, Tooltip, Typography } from '@mui/material';
import Iconify from './Iconify';
import { summarizeMeetingStatus } from '../helpers/meetingStatus';

// Tooltip reutilizable con el desglose de estados de las reuniones (statusCounts).
// Envuelve cualquier nodo (el número total de reuniones) y muestra al pasar el ratón:
//  - un resumen por categoría semántica (Éxito / En curso / En espera / Errores)
//  - el detalle de los estados concretos presentes dentro de cada categoría.
// Si además se pasa `deletedStatusCounts`, debajo se añade un segundo bloque con las
// reuniones eliminadas (el estado es el que tenían en el momento del borrado).
// Si no hay nada que mostrar (ni vivas ni borradas), renderiza los children sin tooltip.

MeetingStatusTooltip.propTypes = {
  statusCounts: PropTypes.object,
  // Desglose por estado de las reuniones borradas (estado al borrarse).
  deletedStatusCounts: PropTypes.object,
  children: PropTypes.element.isRequired,
  // Texto opcional de cabecera del bloque de reuniones vivas.
  title: PropTypes.string,
  // Texto opcional de cabecera del bloque de reuniones borradas.
  deletedTitle: PropTypes.string,
  placement: PropTypes.string,
};

// El color 'default' no tiene .main fiable; lo mapeamos a un gris.
const dotColor = (color) => (color === 'default' ? 'text.disabled' : `${color}.main`);

// Renderiza un bloque (cabecera + grupos + detalle) a partir de un resumen ya normalizado.
function StatusSection({ title, groups }) {
  return (
    <Box>
      <Typography variant="caption" sx={{ color: 'common.white', opacity: 0.7 }}>
        {title}
      </Typography>
      <Stack spacing={1} sx={{ mt: 0.75 }}>
        {groups.map((g) => (
          <Box key={g.key}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Iconify icon={g.icon} width={14} height={14} sx={{ color: dotColor(g.color) }} />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {g.label}
                </Typography>
              </Stack>
              <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                {g.count}
              </Typography>
            </Stack>
            {g.items.map((it) => (
              <Stack
                key={it.key}
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={2}
                sx={{ pl: 2.25 }}
              >
                <Typography variant="caption" sx={{ opacity: 0.85 }}>
                  {it.label}
                </Typography>
                <Typography variant="caption" sx={{ fontVariantNumeric: 'tabular-nums', opacity: 0.85 }}>
                  {it.count}
                </Typography>
              </Stack>
            ))}
          </Box>
        ))}
      </Stack>
    </Box>
  );
}

StatusSection.propTypes = {
  title: PropTypes.node,
  groups: PropTypes.array,
};

export default function MeetingStatusTooltip({
  statusCounts,
  deletedStatusCounts,
  children,
  title = 'Desglose por estado (histórico)',
  deletedTitle = 'Eliminadas · estado al borrarse',
  placement = 'top',
}) {
  const alive = summarizeMeetingStatus(statusCounts);
  const deleted = summarizeMeetingStatus(deletedStatusCounts);

  if (alive.total === 0 && deleted.total === 0) return children;

  const content = (
    <Box sx={{ py: 0.5, minWidth: 180 }}>
      {alive.total > 0 && <StatusSection title={title} groups={alive.groups} />}
      {deleted.total > 0 && (
        <>
          {alive.total > 0 && <Divider sx={{ my: 1, borderColor: 'rgba(255,255,255,0.16)' }} />}
          <StatusSection
            title={
              <Stack direction="row" alignItems="center" spacing={0.5} component="span">
                <Iconify icon="eva:trash-2-outline" width={13} height={13} />
                <span>{deletedTitle}</span>
              </Stack>
            }
            groups={deleted.groups}
          />
        </>
      )}
    </Box>
  );

  return (
    <Tooltip title={content} placement={placement} arrow>
      {children}
    </Tooltip>
  );
}
